import { Card, Input } from '@nextui-org/react'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import MainButton from '../common/util/input/MainButton'
import { get } from '../helpers/Api'
import { formatNumber } from '../helpers/FormatLinks'
import { getBoostAction } from '../helpers/WaxApi'
import LoadingIndicator from '../loadingindicator/LoadingIndicator'
import { PopupLoadingIndicator } from '../loadingindicator/PopupLoadingIndicator'
import ErrorMessage from './ErrorMessage'
import Popup from './Popup'
import { useSharedState } from '../waxplorer/Waxplorer'

function AuctionPopup(props) {
    const asset = props['asset']
    const { t } = useTranslation('common')

    const { assetId, schema, collection, name, image, contract } = asset

    const [state] = useSharedState()

    const activeUser = state?.activeUser

    const userName = activeUser?.accountName
        ? state?.activeUser.accountName
        : null

    const callBack = props['callBack']
    const offer = props['offer']

    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState()
    const marketplace =
        contract === 'atomicassets' ? 'atomicmarket' : 'waxplorercom'
    const closeCallBack = props['closeCallBack']
    const [collectionFee, setCollectionFee] = useState(null)
    const [sellPrice, setSellPrice] = useState(offer)
    const [days, setDays] = useState(1)
    const [hours, setHours] = useState(0)
    const [minutes, setMinutes] = useState(0)

    const getCollectionFee = async (collectionName) => {
        get('collection-fee/' + collectionName, 'wax-api').then((result) =>
            setCollectionFee(result['fee']),
        )
    }

    useEffect(() => {
        getCollectionFee(collection.collectionName)
    }, [])

    const getAuctionId = (result, assetId) => {
        if (result?.response?.processed?.action_traces) {
            for (const action of result.response.processed.action_traces) {
                if (
                    action.act.name === 'lognewauct' &&
                    action.act.data.asset_ids.includes(assetId.toString())
                ) {
                    return action.act.data.auction_id
                }
            }
        }

        return null
    }

    const sell = async () => {
        if (!sellPrice) return
        const quantity = parseFloat(sellPrice)
        const { assetId } = asset
        closeCallBack()
        setIsLoading(true)
        try {
            const result = await activeUser.session.transact(
                {
                    actions: [
                        getBoostAction(activeUser),
                        {
                            account: 'atomicmarket',
                            name: 'announceauct',
                            authorization: [
                                {
                                    actor: userName,
                                    permission: activeUser.requestPermission
                                        ? activeUser.requestPermission
                                        : 'active',
                                },
                            ],
                            data: {
                                duration:
                                    (days ? parseInt(days) * 24 * 60 * 60 : 0) +
                                    (hours ? parseInt(hours) * 60 * 60 : 0) +
                                    (minutes ? parseInt(minutes) * 60 : 0),
                                starting_bid: quantity.toFixed(8) + ' WAX',
                                seller: userName,
                                maker_marketplace:
                                    process.env.NEXT_PUBLIC_TESTNET === 'TRUE'
                                        ? 'nfthiveboost'
                                        : 'nft.hive',
                                asset_ids: [assetId],
                            },
                        },
                        {
                            account: 'atomicassets',
                            name: 'transfer',
                            authorization: [
                                {
                                    actor: userName,
                                    permission: activeUser.requestPermission
                                        ? activeUser.requestPermission
                                        : 'active',
                                },
                            ],
                            data: {
                                from: userName,
                                memo: 'auction',
                                asset_ids: [assetId],
                                to: 'atomicmarket',
                            },
                        },
                    ],
                },
                {
                    expireSeconds: 300,
                    blocksBehind: 0,
                },
            )
            let auctionId = getAuctionId(result, assetId)
            callBack({
                success: true,
                auction: {
                    assetId: assetId,
                    market: marketplace,
                    seller: userName,
                    currency: 'WAX',
                    price: quantity,
                    auctionId: auctionId,
                },
                transactionId: result?.response?.transaction_id,
            })
        } catch (e) {
            callBack({
                success: false,
                auction: null,
                error: e.message,
            })
            console.error(e)
            setError(e.message)
        } finally {
            setIsLoading(false)
        }
    }

    const cancel = () => {
        callBack({
            success: false,
            auction: null,
        })
        closeCallBack()
    }

    let cut = sellPrice - 0.03 * sellPrice
    if (collectionFee) cut = cut - collectionFee * sellPrice

    const changePrice = (e) => {
        const val = e.target.value
        if (/^\d*\.?\d*$/.test(val)) setSellPrice(val)
    }

    const changeHours = (e) => {
        const val = e.target.value
        if (/^\d*$/.test(val)) setHours(val)
    }

    const changeMinutes = (e) => {
        const val = e.target.value
        if (/^\d*$/.test(val)) setMinutes(val)
    }

    const changeDays = (e) => {
        const val = e.target.value
        if (/^\d*$/.test(val)) setDays(val)
    }

    return (
        <Popup title={name} cancel={cancel} image={image}>
            <div className="mx-auto mb-4 text-xl font-bold">
                {t('popups.are_you_sure_you_want_to_auction_asset_for_amount', {
                    asset: name,
                    minutes: minutes,
                    hours: hours,
                    days: days,
                    amount: `${formatNumber(sellPrice)} WAX}`,
                })}
            </div>
            <Card className="p-4">
                <div className="grid grid-cols-2 ">
                    <div className="flex flex-col gap-2 ">
                        <b>{t('asset.schema')}:</b>
                        <b>{t('asset.author')}:</b>
                        <b>{t('asset.lowest_available_price')}:</b>
                        <b>{t('asset.average_bought')}:</b>
                        <b>{t('asset.last_sold')}:</b>
                        <b>{t('asset.price')}:</b>
                    </div>
                    <div className="flex flex-col gap-2 t">
                        <div>{schema}</div>

                        <div>{collection.collectionName}</div>
                    </div>
                </div>
            </Card>

            {error ? <ErrorMessage error={error} /> : ''}
            <div className="grid grid-cols-2 gap-2 mt-4 md:grid-cols-4">
                <div>
                    <Input
                        variant="faded"
                        type="number"
                        onWheel={(e) => e.target.blur()}
                        label={t('search.start_bid')}
                        onChange={changePrice}
                        value={sellPrice ? sellPrice : ''}
                    />
                </div>
                <div>
                    <Input
                        variant="faded"
                        type="number"
                        onWheel={(e) => e.target.blur()}
                        label={t('popups.days')}
                        onChange={changeDays}
                        value={days ? days : ''}
                    />
                </div>
                <div>
                    <Input
                        variant="faded"
                        type="number"
                        onWheel={(e) => e.target.blur()}
                        label={t('popups.hours')}
                        onChange={changeHours}
                        value={hours ? hours : ''}
                    />
                </div>
                <div>
                    <Input
                        variant="faded"
                        type="number"
                        onWheel={(e) => e.target.blur()}
                        label={t('popups.minutes')}
                        onChange={changeMinutes}
                        value={minutes ? minutes : ''}
                    />
                </div>
            </div>
            <div className={'my-4 flex justify-start'}>
                <MainButton
                    onClick={cancel}
                    className={'w-32 bg-red-500 max-w-32'}
                >
                    {t('asset.cancel')}
                </MainButton>
                <MainButton
                    disabled={
                        !sellPrice ||
                        ((!days || days === '0') &&
                            (!hours || hours === '0') &&
                            (!minutes || minutes === '0'))
                            ? 'disabled'
                            : ''
                    }
                    className={'w-32 max-w-32'}
                    padding={'pl-4'}
                    onClick={sell}
                >
                    {t('popups.auction')}
                </MainButton>
            </div>
            {collectionFee || collectionFee === 0 ? (
                <div className="grid grid-cols-2 gap-2 mt-4 md:grid-cols-4 ">
                    <div className="flex flex-col ">
                        <div>{t('popups.market_fee')}:</div>
                        <div>2%</div>
                    </div>
                    <div className="flex flex-col ">
                        <div>{t('popups.defi_fee')}:</div>
                        <div>1%</div>
                    </div>
                    <div className="flex flex-col ">
                        <div>{t('popups.collection_fee')}:</div>
                        <div>{collectionFee * 100}%</div>
                    </div>
                    <div className="flex flex-col ">
                        <div>{t('popups.your_cut')}:</div>
                        <div>{cut} WAX</div>
                    </div>
                </div>
            ) : (
                <LoadingIndicator />
            )}

            {isLoading ? (
                <PopupLoadingIndicator
                    text={t('popups.loading_transaction')}
                    isLoading={isLoading}
                />
            ) : (
                ''
            )}
        </Popup>
    )
}

export default AuctionPopup
